import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, API_BASE, getAccessToken, newKey, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, Upload, Plus, Sparkles, Scale } from "lucide-react";

const SESSION_KEY = "ledger_ai_session";
const EXAMPLES = [
  "Which parties owe the most right now?",
  "Which bills have a payment entered on the day the bill was created?",
  "How much is pending with each employee?",
  "List bills older than 30 days that are unpaid.",
];

async function streamChat(body, onDelta) {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}/ai/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.detail || `Request failed (${res.status})`);
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      let ev;
      try {
        ev = JSON.parse(p.slice(6));
      } catch (e) {
        console.warn("Skipping malformed stream event", e);
        continue;
      }
      if (ev.error) throw new Error(ev.error);
      if (ev.delta) onDelta(ev.delta);
    }
  }
}

function Bubble({ m, i }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${i}`}>
      <div className={`max-w-[92%] rounded-2xl px-4 py-3 text-sm ${mine ? "bg-[#175C3B] text-white whitespace-pre-wrap" : "bg-white border border-neutral-200 ai-md"}`}>
        {mine ? (m.content.length > 600 ? `${m.content.slice(0, 600)}… (${m.content.length} characters sent)` : m.content)
          : <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content || "…"}</ReactMarkdown>}
      </div>
    </div>
  );
}

function ModeTabs({ mode, setMode }) {
  const tab = (id, label, Icon) => (
    <button type="button" onClick={() => setMode(id)} data-testid={`ai-mode-${id}`}
      className={`rounded-full px-4 py-2 text-sm flex items-center gap-2 transition-colors ${mode === id ? "bg-neutral-900 text-white" : "border border-neutral-200 hover:bg-neutral-100"}`}>
      <Icon size={14} /> {label}
    </button>
  );
  return (
    <div className="flex flex-wrap gap-2">
      {tab("chat", "Ask about the ledger", Sparkles)}
      {tab("compare", "Compare with my records", Scale)}
    </div>
  );
}

function Composer({ mode, busy, onSend }) {
  const [text, setText] = useState("");
  const fileRef = useRef(null);
  const send = () => {
    if (!text.trim() || busy) return;
    onSend(text.trim());
    setText("");
  };
  const loadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setText(await f.text());
    e.target.value = "";
  };
  return (
    <div className="card-surface p-3 flex flex-col gap-2">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={mode === "compare" ? 7 : 2}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && mode === "chat") { e.preventDefault(); send(); } }}
        placeholder={mode === "compare" ? "Paste your dad's register here (bill no, amount, received — one bill per line), or upload a CSV / text file." : "Ask anything about bills, parties, employees or payments…"}
        className="w-full resize-y bg-transparent outline-none text-sm p-2" data-testid="ai-input" />
      <div className="flex justify-between items-center">
        {mode === "compare" ? (
          <>
            <input ref={fileRef} type="file" accept=".csv,.txt,.tsv" className="hidden" onChange={loadFile} data-testid="ai-file-input" />
            <button type="button" onClick={() => fileRef.current?.click()} className="text-sm flex items-center gap-2 text-neutral-600 hover:text-neutral-900" data-testid="ai-upload-btn">
              <Upload size={14} /> Upload CSV / text
            </button>
          </>
        ) : <span className="text-xs text-neutral-400">Enter to send · Shift+Enter for a new line</span>}
        <button type="button" onClick={send} disabled={busy || !text.trim()} className="btn-primary py-2 px-5 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="ai-send-btn">
          <Send size={14} /> {busy ? "Thinking…" : mode === "compare" ? "Compare" : "Send"}
        </button>
      </div>
    </div>
  );
}

export default function Assistant() {
  const [session, setSession] = useState(() => localStorage.getItem(SESSION_KEY) || newKey());
  const [msgs, setMsgs] = useState([]);
  const [mode, setMode] = useState("chat");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      setMsgs((await api.get(`/ai/sessions/${session}/messages`)).data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [session]);

  useEffect(() => {
    localStorage.setItem(SESSION_KEY, session);
    loadHistory();
  }, [session, loadHistory]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (text) => {
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      await streamChat({ session_id: session, message: text, mode }, (d) =>
        setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content: m[m.length - 1].content + d }]));
    } catch (e) {
      toast.error(e.message);
      setMsgs((m) => m.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 lg:p-14 max-w-[1100px] mx-auto flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.35em] text-neutral-500">Owner only · Claude</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">Ledger Assistant</h1>
          <p className="text-sm text-neutral-500 mt-2">Reads the live ledger. It can explain and compare, but never changes data.</p>
        </div>
        <button type="button" onClick={() => setSession(newKey())} className="rounded-full border border-neutral-200 px-4 py-2 text-sm flex items-center gap-2 hover:bg-neutral-100" data-testid="ai-new-chat-btn">
          <Plus size={14} /> New chat
        </button>
      </div>
      <ModeTabs mode={mode} setMode={setMode} />
      <div className="flex flex-col gap-3 min-h-[200px]" data-testid="ai-messages">
        {msgs.length === 0 && (
          <div className="flex flex-wrap gap-2" data-testid="ai-examples">
            {EXAMPLES.map((q) => (
              <button key={q} type="button" onClick={() => send(q)} disabled={busy} className="chip border border-neutral-200 hover:bg-neutral-100 text-sm">{q}</button>
            ))}
          </div>
        )}
        {msgs.map((m, i) => <Bubble key={m.id || `${i}-${m.role}`} m={m} i={i} />)}
        <div ref={endRef} />
      </div>
      <Composer mode={mode} busy={busy} onSend={send} />
    </div>
  );
}
